import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Copy, MessageSquare, Share, Check, Mail, Send, Loader2, CheckCircle2, Paperclip, AlertCircle } from 'lucide-react';
import { SendSmsModal } from './SendSmsModal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { copyToClipboard, nativeShare, showAlert } from '@/lib/capacitor';
import { apiService } from '@/lib/api';
import { fillTemplate } from '@/lib/emailTemplate';
import { composeSms } from '@/lib/smsTemplate';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useBackNavigationLayer } from '@/lib/navigation';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';

export type ShareEntityType = 'invoice' | 'estimate';

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSent?: () => void;
    shareUrl: string;
    recipientPhone: string;
    recipientName: string;
    recipientEmail?: string;
    entityType: ShareEntityType;
    entityNumber: string;
    /** Invoice only: fully-paid invoices get a view-only SMS template (no payment prompt). */
    isFullyPaid?: boolean;
    /** Pre-formatted amount (e.g. "$340.00") woven into the SMS. Omit when zero or unknown. */
    amountLabel?: string;
    /** Linked job — technicians must send SMS in a job they are assigned to. */
    jobId?: string;
    /** Estimate share has no job; technicians send this instead of jobId. */
    estimateId?: string;
}

const entityLabels: Record<ShareEntityType, { singular: string; action: string }> = {
    invoice: { singular: 'Invoice', action: 'view and sign your invoice' },
    estimate: { singular: 'Estimate', action: 'view and sign your estimate' },
};

// Fixed, non-editable SMS templates. Company name comes from invoice settings
// (display_name), falling back to the companies-table name.
//
// Carrier A2P filters reject near-identical bulk bodies (Telnyx 40002), so the number and the
// amount stay in: they are what make each send look like the transactional message it is.
// Keep the body strictly GSM-7 - no '&', no curly quotes, no emoji. One non-GSM-7 character
// flips the whole message to UCS-2, halves the segment to 70 chars and splits it, and multipart
// bodies are themselves a filtering signal. composeSms adds the company prefix and the STOP line:
// sender identification and a working opt-out are CTIA requirements, and filters check for them.
function buildSmsTemplate(
    entityType: ShareEntityType,
    isFullyPaid: boolean,
    companyName: string,
    shareUrl: string,
    entityNumber: string,
    amountLabel?: string,
): string {
    const subject = entityNumber ? `${entityLabels[entityType].singular} #${entityNumber}` : `Your ${entityType}`;
    const amount = amountLabel ? ` for ${amountLabel}` : '';
    const action = entityType === 'estimate' ? 'view and approve' : isFullyPaid ? 'view' : 'view and pay';
    return composeSms(companyName, `${subject}${amount} is ready to ${action}.\n${shareUrl}`);
}

export function ShareModal({
    isOpen,
    onClose,
    onSent,
    shareUrl,
    recipientPhone,
    recipientName,
    recipientEmail = '',
    entityType,
    entityNumber,
    isFullyPaid = false,
    amountLabel,
    jobId,
    estimateId,
}: ShareModalProps) {
    const isMobile = useIsMobile();
    const { selectedCompanyId, selectedCompany } = useGlobalCompany();
    const { data: invoiceSettings } = useQuery({
        queryKey: ['company-invoice-settings', selectedCompanyId],
        queryFn: () => apiService.getCompanyInvoiceSettings(),
        enabled: isOpen && !!selectedCompanyId,
        staleTime: 5 * 60 * 1000,
    });
    const [showSmsModal, setShowSmsModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const layer = useModalLayer({
        enabled: isOpen && !showSmsModal && !showEmailModal,
        onClose,
        historyState: { shareModal: true },
    });
    const handleEmailModalClose = useBackNavigationLayer({
        enabled: showEmailModal,
        onBack: () => {
            setShowEmailModal(false);
            onClose();
        },
        state: { shareEmailModal: true },
    });

    const labels = entityLabels[entityType];

    const handleCopyLink = async () => {
        const success = await copyToClipboard(shareUrl);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleServiceFalconSms = () => {
        setShowSmsModal(true);
    };

    const handleNativeShare = async () => {
        const result = await nativeShare({
            title: `${labels.singular} #${entityNumber}`,
            text: `Please ${labels.action} here:`,
            url: shareUrl,
        });
        if (result === 'shared') {
            onClose();
        } else if (result === 'unavailable') {
            // No share sheet on this device — copy the link instead.
            const ok = await copyToClipboard(shareUrl);
            await showAlert(ok ? 'Link copied to clipboard.' : 'Unable to share on this device.', 'Share');
            if (ok) onClose();
        }
    };

    const handleSmsModalClose = () => {
        setShowSmsModal(false);
        onClose();
    };

    const companyName = (invoiceSettings?.display_name?.trim() || selectedCompany?.name || '').trim();
    const shareMessage = buildSmsTemplate(entityType, isFullyPaid, companyName, shareUrl, entityNumber, amountLabel);

    if (!isOpen && !showSmsModal && !showEmailModal) return null;

    return (
        <LayerDepthContext.Provider value={layer.depth}>
            {isOpen && !showSmsModal && !showEmailModal && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={layer.style}>
                    <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto animate-in fade-in slide-in-from-bottom duration-200 safe-area-bottom">
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-green-50 to-emerald-50">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <Share className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">Share {labels.singular}</h2>
                                    <p className="text-sm text-gray-500">{labels.singular} #{entityNumber}</p>
                                </div>
                            </div>
                            <button
                                onClick={layer.onClose}
                                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Share URL Preview */}
                        <div className="px-4 pt-4">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Share Link</p>
                                <p className="text-sm text-gray-700 truncate font-mono">{shareUrl}</p>
                            </div>
                        </div>

                        {/* Options */}
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-600 mb-4">Choose how to share:</p>

                            {/* Copy Link */}
                            <button
                                onClick={handleCopyLink}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all group"
                            >
                                <div className={`p-3 rounded-xl transition-colors ${copied ? 'bg-green-200' : 'bg-gray-100 group-hover:bg-gray-200'}`}>
                                    {copied ? (
                                        <Check className="h-6 w-6 text-green-600" />
                                    ) : (
                                        <Copy className="h-6 w-6 text-gray-600" />
                                    )}
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">
                                        {copied ? 'Link Copied!' : 'Copy Link'}
                                    </p>
                                    <p className="text-sm text-gray-500">Copy to clipboard to paste anywhere</p>
                                </div>
                            </button>

                            {/* SMS via Service Strike */}
                            {recipientPhone && (
                                <button
                                    onClick={handleServiceFalconSms}
                                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all group"
                                >
                                    <div className="p-3 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors">
                                        <MessageSquare className="h-6 w-6 text-blue-600" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">Send via SMS</p>
                                        <p className="text-sm text-gray-500">Send SMS using your VoIP number</p>
                                    </div>
                                </button>
                            )}

                            {/* Email */}
                            <button
                                onClick={() => setShowEmailModal(true)}
                                className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all group"
                            >
                                <div className="p-3 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors">
                                    <Mail className="h-6 w-6 text-purple-600" />
                                </div>
                                <div className="text-left">
                                    <p className="font-semibold text-gray-900">Send via Email</p>
                                    <p className="text-sm text-gray-500">Send the {entityType} link by email</p>
                                </div>
                            </button>

                            {/* Native Share */}
                            {isMobile && (
                                <button
                                    onClick={handleNativeShare}
                                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                                >
                                    <div className="p-3 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
                                        <Share className="h-6 w-6 text-indigo-600" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">More Options</p>
                                        <p className="text-sm text-gray-500">Share via other apps on your device</p>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Cancel Button */}
                        <div className="p-4 pt-0">
                            <button
                                onClick={layer.onClose}
                                className="w-full py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VoIP SMS Modal */}
            <SendSmsModal
                isOpen={showSmsModal}
                onClose={handleSmsModalClose}
                onSent={onSent}
                recipientPhone={recipientPhone}
                recipientName={recipientName || 'Client'}
                recipientType="Client"
                initialMessage={shareMessage}
                lockMessage
                jobId={jobId}
                estimateId={estimateId}
            />

            {/* Email Modal */}
            {showEmailModal && (
                <ShareEmailModal
                    entityType={entityType}
                    entityNumber={entityNumber}
                    shareUrl={shareUrl}
                    recipientEmail={recipientEmail}
                    recipientName={recipientName}
                    onClose={handleEmailModalClose}
                    onSent={onSent}
                />
            )}
        </LayerDepthContext.Provider>
    );
}

// Generic Email Modal Component
interface ShareEmailModalProps {
    entityType: ShareEntityType;
    entityNumber: string;
    shareUrl: string;
    recipientEmail: string;
    recipientName: string;
    onClose: () => void;
    onSent?: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;  // 5 MB
const MAX_TOTAL_SIZE = 15 * 1024 * 1024; // 15 MB
const MAX_FILE_COUNT = 5;
const ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'image/png', 'image/jpeg', 'image/gif', 'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv', 'text/plain',
]);
const ALLOWED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt';

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ShareEmailModal({ entityType, entityNumber, shareUrl, recipientEmail, recipientName, onClose, onSent }: ShareEmailModalProps) {
    const { userRole } = useCurrentUser();
    const isTechnician = userRole?.toLowerCase() === 'technician';
    const labels = entityLabels[entityType];
    const [to, setTo] = useState(recipientEmail);
    const [subject, setSubject] = useState(`${labels.singular} #${entityNumber}`);
    const [body, setBody] = useState(
        `Hi${recipientName ? ` ${recipientName}` : ''},\n\nPlease ${labels.action} here:\n\nThank you!`
    );
    const [buttonLabel, setButtonLabel] = useState(`View ${labels.singular}`);
    const [isSending, setIsSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [gmailConnected, setGmailConnected] = useState<boolean | null>(null);
    const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
    const [serviceStrikeStatus, setServiceStrikeStatus] = useState<{ is_configured: boolean; sender_name?: string; available_from_addresses: { context: string; address: string }[] } | null>(null);
    const [senderType, setSenderType] = useState<'gmail' | 'service_strike'>('gmail');
    const [attachments, setAttachments] = useState<{ file: File; base64: string }[]>([]);
    const [attachmentError, setAttachmentError] = useState<string | null>(null);
    const [readingFiles, setReadingFiles] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounter = useRef(0);
    const handleEmailClose = useBackNavigationLayer({
        enabled: true,
        onBack: onClose,
        state: { shareEmailModalContent: true },
    });

    const checkEmailStatus = useCallback(async () => {
        try {
            setError(null);
            const [status, defaults, template] = await Promise.all([
                apiService.getEmailStatus(),
                apiService.getEmailDefaults().catch(() => null),
                apiService.getEmailTemplate(entityType).catch(() => null),
            ]);
            setGmailConnected(status.is_connected);
            setConnectedEmail(status.email_address || null);
            setServiceStrikeStatus(status.service_strike ?? { is_configured: false, available_from_addresses: [] });

            // Auto-select sender type: prefer the company default for customer communication,
            // falling back to whatever is actually available (gmail first).
            const gmailOk = status.is_connected;
            const ssOk = status.service_strike?.is_configured ?? false;
            const preferred = defaults?.customer_method ?? null;
            const pick =
                (preferred === 'gmail' && gmailOk) || (preferred === 'service_strike' && ssOk)
                    ? preferred
                    : gmailOk
                        ? 'gmail'
                        : ssOk
                            ? 'service_strike'
                            : 'gmail';
            setSenderType(pick as 'gmail' | 'service_strike');

            // Pre-fill from a company-customized template. When none is set, keep the existing defaults.
            if (template?.is_custom) {
                const vars = { customer_name: recipientName, number: entityNumber, link: shareUrl };
                setSubject(fillTemplate(template.subject, vars));
                setBody(fillTemplate(template.body, vars));
                if (template.button_label) setButtonLabel(fillTemplate(template.button_label, vars));
            }
        } catch {
            // Set a deterministic (non-null) state so the UI can render the error path
            // instead of being stuck on the loader or wrongly showing "Not Configured".
            setGmailConnected(false);
            setConnectedEmail(null);
            setServiceStrikeStatus({ is_configured: false, available_from_addresses: [] });
            setError('Could not check email availability. Please try again.');
        }
    }, [entityType, entityNumber, shareUrl, recipientName]);

    useEffect(() => {
        checkEmailStatus();
    }, [checkEmailStatus]);

    const totalAttachmentSize = attachments.reduce((sum, a) => sum + a.file.size, 0);
    const isServiceStrike = senderType === 'service_strike';
    const hasEmailAvailable = gmailConnected || serviceStrikeStatus?.is_configured;
    const bothAvailable = gmailConnected && serviceStrikeStatus?.is_configured;
    const currentFromAddress = isServiceStrike
        ? serviceStrikeStatus?.available_from_addresses.find(a => a.context === entityType)?.address ?? 'noreply@servicestrike.com'
        : connectedEmail || '';

    // Determine the "sending from" label
    const getSendingFromLabel = () => {
        if (isServiceStrike) {
            return `Service Strike (${currentFromAddress})`;
        }
        return connectedEmail || 'Gmail';
    };

    const getEmailContext = (): string | undefined => {
        if (isServiceStrike) {
            return entityType;
        }
        return undefined;
    };

    const handleFilesSelected = useCallback(async (files: FileList | File[]) => {
        setAttachmentError(null);
        const fileArray = Array.from(files);
        const errors: string[] = [];
        const validFiles: File[] = [];

        for (const file of fileArray) {
            if (!ALLOWED_MIME_TYPES.has(file.type)) {
                errors.push(`${file.name}: file type not allowed`);
                continue;
            }
            if (file.size > MAX_FILE_SIZE) {
                errors.push(`${file.name}: exceeds 5 MB limit`);
                continue;
            }
            validFiles.push(file);
        }

        // Check count limit against current + new
        const availableSlots = MAX_FILE_COUNT - attachments.length;
        if (validFiles.length > availableSlots) {
            const dropped = validFiles.splice(availableSlots);
            errors.push(`${dropped.map(f => f.name).join(', ')}: max ${MAX_FILE_COUNT} files allowed`);
        }

        // Check total size
        let currentTotal = attachments.reduce((sum, a) => sum + a.file.size, 0);
        const sizeFiltered: File[] = [];
        for (const file of validFiles) {
            if (currentTotal + file.size > MAX_TOTAL_SIZE) {
                errors.push(`${file.name}: would exceed 15 MB total limit`);
            } else {
                currentTotal += file.size;
                sizeFiltered.push(file);
            }
        }

        if (errors.length > 0) {
            setAttachmentError(errors.join('; '));
        }

        if (sizeFiltered.length === 0) return;

        setReadingFiles(true);
        try {
            const results = await Promise.all(
                sizeFiltered.map(
                    (file) =>
                        new Promise<{ file: File; base64: string }>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => {
                                const dataUrl = reader.result as string;
                                const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
                                resolve({ file, base64 });
                            };
                            reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
                            reader.readAsDataURL(file);
                        })
                )
            );
            setAttachments((prev) => [...prev, ...results]);
        } catch {
            setAttachmentError('Failed to read one or more files');
        } finally {
            setReadingFiles(false);
        }
    }, [attachments]);

    const removeAttachment = useCallback((index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
        setAttachmentError(null);
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;
        if (e.dataTransfer.files.length > 0) {
            handleFilesSelected(e.dataTransfer.files);
        }
    }, [handleFilesSelected]);

    const handleSend = async () => {
        if (!to || !subject) {
            setError('Please fill in the recipient and subject');
            return;
        }

        try {
            setIsSending(true);
            setError(null);
            await apiService.sendEmail({
                to,
                subject,
                body,
                sender_type: senderType,
                email_context: getEmailContext(),
                template: {
                    name: 'share_link',
                    button_url: shareUrl,
                    button_label: buttonLabel,
                },
                ...(attachments.length > 0 && {
                    attachments: attachments.map((a) => ({
                        filename: a.file.name,
                        mime_type: a.file.type || 'application/octet-stream',
                        data: a.base64,
                    })),
                }),
            });
            setSendSuccess(true);
            setAttachments([]);
            setAttachmentError(null);
            onSent?.();
        } catch {
            setError('Failed to send email. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[120] p-0 sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl overflow-hidden flex flex-col max-h-[90vh] safe-area-bottom">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-purple-600 to-indigo-600">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Share {labels.singular} via Email
                    </h3>
                    <button onClick={handleEmailClose} className="text-white/80 hover:text-white p-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto flex-1">
                    {(gmailConnected === null && serviceStrikeStatus === null) && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                        </div>
                    )}

                    {/* Connection error — the status check itself failed */}
                    {error && !hasEmailAvailable && gmailConnected !== null && (
                        <div className="text-center py-8">
                            <AlertCircle className="h-12 w-12 text-red-300 mx-auto mb-4" />
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Connection Error</h4>
                            <p className="text-sm text-gray-500 mb-4">{error}</p>
                            <div className="flex items-center justify-center gap-3">
                                <button onClick={() => checkEmailStatus()} className="btn-primary">Retry</button>
                                <button onClick={handleEmailClose} className="btn-secondary">Close</button>
                            </div>
                        </div>
                    )}

                    {/* No email configured — status loaded fine but nothing is set up */}
                    {!error && !hasEmailAvailable && gmailConnected !== null && serviceStrikeStatus !== null && (
                        <div className="text-center py-8">
                            <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Email Not Configured</h4>
                            <p className="text-sm text-gray-500 mb-4">
                                No email is configured for this company. Please connect a Gmail account or configure Service Strike email in settings.
                            </p>
                            <button onClick={handleEmailClose} className="btn-secondary">Close</button>
                        </div>
                    )}

                    {sendSuccess && (
                        <div className="text-center py-8">
                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Email Sent</h4>
                            <p className="text-sm text-gray-500 mb-4">
                                The {entityType} link has been sent to {to}{attachments.length > 0 ? ` with ${attachments.length} attachment${attachments.length === 1 ? '' : 's'}` : ''}.
                            </p>
                            <button onClick={handleEmailClose} className="btn-primary">Done</button>
                        </div>
                    )}

                    {hasEmailAvailable && !sendSuccess && (
                        <div className="space-y-4">
                            {/* Sender Selection */}
                            {bothAvailable && !isTechnician && (
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <label className="block text-xs text-gray-500 uppercase font-medium mb-2">Send Using</label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => { setSenderType('gmail'); setAttachmentError(null); }}
                                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                                senderType === 'gmail'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-purple-50'
                                            }`}
                                        >
                                            Gmail {connectedEmail ? `(${connectedEmail})` : ''}
                                        </button>
                                        <button
                                            onClick={() => { setSenderType('service_strike'); setAttachments([]); }}
                                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                                                senderType === 'service_strike'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-purple-50'
                                            }`}
                                        >
                                            Service Strike
                                        </button>
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-gray-500">
                                Sending from <span className="font-medium">{getSendingFromLabel()}</span>
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                                <input
                                    type="email"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    placeholder="recipient@example.com"
                                    className="input-field w-full"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="input-field w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                <textarea
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    className="input-field w-full min-h-[300px] resize-y"
                                />
                            </div>

                            {/* Attachments — not supported for Service Strike */}
                            {!isServiceStrike && <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
                                <label
                                    className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                        isDragging
                                            ? 'border-purple-400 bg-purple-50'
                                            : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                                    } ${attachments.length >= MAX_FILE_COUNT ? 'opacity-50 pointer-events-none' : ''}`}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <Paperclip className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm text-gray-500">
                                        {isDragging ? 'Drop files here' : 'Attach files'}
                                    </span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept={ALLOWED_EXTENSIONS}
                                        className="hidden"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files.length > 0) {
                                                handleFilesSelected(e.target.files);
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                </label>

                                {attachments.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        {attachments.map((a, i) => (
                                            <div
                                                key={`${a.file.name}-${i}`}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm"
                                            >
                                                <Paperclip className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                                <span className="truncate text-gray-700">{a.file.name}</span>
                                                <span className="text-gray-400 text-xs flex-shrink-0">
                                                    {formatFileSize(a.file.size)}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAttachment(i)}
                                                    className="ml-auto p-0.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                                                >
                                                    <X className="h-3.5 w-3.5 text-gray-500" />
                                                </button>
                                            </div>
                                        ))}
                                        <p className="text-xs text-gray-400 mt-1">
                                            {attachments.length} file{attachments.length === 1 ? '' : 's'} ({formatFileSize(totalAttachmentSize)} / {formatFileSize(MAX_TOTAL_SIZE)})
                                        </p>
                                    </div>
                                )}

                                {readingFiles && (
                                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        Reading files...
                                    </div>
                                )}

                                {attachmentError && (
                                    <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600">{attachmentError}</div>
                                )}
                            </div>}

                            {error && (
                                <div className="p-3 bg-red-50 rounded-lg text-sm text-red-600">{error}</div>
                            )}
                        </div>
                    )}
                </div>

                {hasEmailAvailable && !sendSuccess && (
                    <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 keyboard-aware-footer">
                        <button onClick={handleEmailClose} className="btn-secondary" disabled={isSending}>
                            Cancel
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={isSending || readingFiles || !to}
                            className="btn-primary flex items-center gap-2"
                        >
                            {isSending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    Send
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ShareModal;
